import React, { useRef, useState } from "react";
import { Upload, ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const ImageField = ({ tid, value, onChange, uploadPath = "/admin/upload" }) => {
  const [progress, setProgress] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file.");
    if (file.size > 8 * 1024 * 1024) return toast.error("Image must be under 8 MB.");
    const fd = new FormData();
    fd.append("file", file);
    setProgress(0);
    try {
      const { data } = await api.post(uploadPath, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => setProgress(Math.round((e.loaded / (e.total || file.size)) * 100)),
      });
      onChange(data.url);
      toast.success("Photo uploaded.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed. Try again.");
    } finally {
      setProgress(null);
    }
  };

  return (
    <div className="space-y-3">
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        data-testid={`${tid}-dropzone`}
        className={`relative flex items-center gap-4 rounded-2xl border-2 border-dashed p-4 cursor-pointer transition-colors ${drag ? "border-[color:var(--coral)] bg-[color:var(--coral)]/5" : "border-[color:var(--line)] bg-white hover:border-[color:var(--ocean)]"}`}
      >
        {value ? (
          <img src={value} alt="" className="w-24 h-24 rounded-xl object-cover flex-shrink-0" />
        ) : (
          <div className="w-24 h-24 rounded-xl bg-[color:var(--sand)] grid place-items-center flex-shrink-0 text-[color:var(--ink-2)]"><ImageIcon size={28} /></div>
        )}
        <div className="flex-1 text-sm">
          <div className="font-semibold text-[color:var(--ocean)] inline-flex items-center gap-2"><Upload size={15} /> {value ? "Replace photo" : "Upload a photo"}</div>
          <div className="text-xs text-[color:var(--ink-2)] mt-1">Drag & drop or click · JPG, PNG, WEBP · up to 8 MB</div>
          {progress !== null && (
            <div className="mt-2 h-1.5 rounded-full bg-[color:var(--sand)] overflow-hidden" data-testid={`${tid}-progress`}>
              <div className="h-full bg-[color:var(--coral)] transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
        {value && (
          <button type="button" onClick={(e) => { e.stopPropagation(); onChange(""); }} data-testid={`${tid}-clear`} className="p-1.5 rounded-full hover:bg-[color:var(--sand)]" title="Remove"><X size={15} /></button>
        )}
        <input ref={inputRef} data-testid={`${tid}-file`} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      <input
        data-testid={tid}
        type="url"
        placeholder="…or paste an image URL"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white border border-[color:var(--line)] rounded-xl px-4 py-2 text-xs outline-none focus:border-[color:var(--ocean)]"
      />
    </div>
  );
};
